import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@bi-studio/shared/hooks';
import { Box, Text, Center, Stack, Title, Image, useMantineColorScheme, rem, Container, Group } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Lock } from 'lucide-react';
import logo from "@bi-studio/shared/assets/logo.png";

export default function GoogleCallbackPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { loginWithGoogle } = useAuth();
    const { colorScheme } = useMantineColorScheme();
    const processedRef = useRef(false);
    const [statusMessage, setStatusMessage] = useState('Securely connecting to Google...');

    useEffect(() => {
        const messages = [
            'Securely connecting to Google...',
            'Exchanging authorization codes...',
            'Verifying tenant identity...',
            'Preparing your workspace...',
            'Setting up your session...'
        ];
        
        let msgIndex = 0;
        const interval = setInterval(() => {
            if (msgIndex < messages.length - 1) {
                msgIndex++;
                setStatusMessage(messages[msgIndex]);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const processCallback = async () => {
            if (processedRef.current) return;
            processedRef.current = true;

            const code = searchParams.get('code');
            const error = searchParams.get('error');

            if (error) {
                Notifications.show({
                    title: 'Login Failed',
                    message: 'Google login was denied or failed.',
                    color: 'red',
                });
                navigate('/login');
                return;
            }

            if (!code) {
                Notifications.show({
                    title: 'Login Failed',
                    message: 'No authorization code received.',
                    color: 'red',
                });
                navigate('/login');
                return;
            }

            try {
                // Determine the correct redirect URI based on the current location
                // This must match exactly what was sent to Google in the initial request
                const redirectUri = `${window.location.origin}/auth/google/callback`;

                // Get company alias from local storage or default to something?
                // The login flow usually starts with a known company context.
                // If we don't have it in the URL, we might need to store it in state/sessionStorage before redirect.
                // For now, let's try to get it from a query param 'state' if we passed it, specifically if we need it.
                // But the current `loginWithGoogle` in `use-auth` takes `companyAlias`.
                // We should pass `companyAlias` in the `state` parameter of the OAuth request.

                // Let's assume we can get it from the `state` param.
                const stateParam = searchParams.get('state');
                let companyAlias = "";

                if (stateParam) {
                    try {
                        const state = JSON.parse(atob(stateParam));
                        companyAlias = state.companyAlias;
                    } catch (e) {
                        // Ignore state parse error
                    }
                }

                // Fallback: If no company alias, we might fail or default. 
                // Currently existing auth relies on user providing it or it being hardcoded/selected.
                // Let's use a stored value if state is missing.
                if (!companyAlias) {
                    companyAlias = localStorage.getItem('last_company_alias') || "bi-studio"; // Default fallback
                }

                await loginWithGoogle(code, redirectUri, companyAlias);
                navigate('/');
            } catch (error) {
                Notifications.show({
                    title: 'Login Failed',
                    message: 'Failed to authenticate with Google.',
                    color: 'red',
                });
                navigate('/login');
            }
        };

        processCallback();
    }, [searchParams, navigate, loginWithGoogle]);

    return (
        <Box
            style={{
                height: '100vh',
                width: '100%',
                background: colorScheme === 'dark'
                    ? 'linear-gradient(135deg, #09090b 0%, #18181b 100%)'
                    : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Animated Background Elements */}
            <motion.div
                animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3],
                }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                style={{
                    position: 'absolute',
                    top: '-10%',
                    right: '-10%',
                    width: '50%',
                    height: '50%',
                    background: 'radial-gradient(circle, var(--mantine-color-blue-1) 0%, transparent 70%)',
                    filter: 'blur(100px)',
                    zIndex: 0,
                    mixBlendMode: colorScheme === 'dark' ? 'screen' : 'multiply'
                }}
            />

            <Center h="100vh" style={{ zIndex: 1, position: 'relative' }}>
                <Container size="xs">
                    <Stack align="center" gap="xl">
                        {/* Glassmorphic Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <Box
                                p={rem(40)}
                                style={{
                                    background: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.6)',
                                    backdropFilter: 'blur(20px)',
                                    borderRadius: 'var(--mantine-radius-xl)',
                                    border: '1px solid var(--modal-border)',
                                    boxShadow: 'var(--mantine-shadow-xl)',
                                    textAlign: 'center',
                                    width: rem(360)
                                }}
                            >
                                <Stack align="center" gap="lg">
                                    {/* Loading Animation Area */}
                                    <Box pos="relative" w={80} h={80}>
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                borderRadius: '50%',
                                                border: '2px solid transparent',
                                                borderTopColor: 'var(--mantine-color-blue-6)',
                                                borderRightColor: 'var(--mantine-color-blue-6)',
                                            }}
                                        />
                                        <Center pos="absolute" inset={0}>
                                            <motion.div
                                                animate={{ scale: [1, 1.1, 1] }}
                                                transition={{ duration: 1.5, repeat: Infinity }}
                                            >
                                                <Lock size={30} strokeWidth={1.5} className="text-blue-600" />
                                            </motion.div>
                                        </Center>
                                    </Box>

                                    <Stack gap={4}>
                                        <Title order={3} fw={700}>Authenticating</Title>
                                        <AnimatePresence mode="wait">
                                            <motion.div
                                                key={statusMessage}
                                                initial={{ opacity: 0, y: 5 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -5 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <Text size="sm" c="dimmed">{statusMessage}</Text>
                                            </motion.div>
                                        </AnimatePresence>
                                    </Stack>

                                    <Group gap="xs" mt="sm">
                                        <ShieldCheck size={14} className="text-green-500" />
                                        <Text size="xs" fw={600} c="dimmed" style={{ letterSpacing: rem(0.5) }}>
                                            BI STUDIO SECURE AUTH
                                        </Text>
                                    </Group>
                                </Stack>
                            </Box>
                        </motion.div>

                        {/* Branding Footer */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                        >
                            <Stack align="center" gap="xs">
                                <Image
                                    src={typeof logo === 'string' ? logo : (logo as any).src}
                                    alt="BI Studio"
                                    h={28}
                                    style={{
                                        mixBlendMode: colorScheme === 'dark' ? 'screen' : 'multiply',
                                        filter: colorScheme === 'dark' ? 'invert(1) hue-rotate(180deg) brightness(1.2)' : 'none',
                                        opacity: 0.5
                                    }}
                                />
                                <Text size="xs" c="dimmed">Protecting your data at every step</Text>
                            </Stack>
                        </motion.div>
                    </Stack>
                </Container>
            </Center>
        </Box>
    );
}
